import React, { createContext, useState, useEffect, useRef } from "react";
import callAPI from "commons/callAPI";
import { S3_URL } from "commons/config";
import { useConfig } from "context/configContext";
import { GlobalStyles } from "theme/globalStyleSheet";
import { DefaultColorTheme } from "theme/colors";
import { ThemeProvider } from "styled-components";
import { useOAT } from "context/OATContext";
import { isV4Template } from "commons/util/coreUtils";

const ColorThemeContext = createContext({});

const ColorThemeProvider = ({ children, setIsColorTheme }) => {
  const { appConfig } = useConfig();
  const [colorTheme, setColorTheme] = useState(DefaultColorTheme);
  const { isOAT, waypointId } = useOAT();
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!appConfig || Object.keys(appConfig).length === 0) {
      setColorTheme(DefaultColorTheme);
      setIsColorTheme(true);
      return;
    }

    fetchColorTheme();
  }, [appConfig, waypointId, isOAT]);

  const fetchColorTheme = async () => {
    const currentRequestId = ++requestIdRef.current;
    try {
      const _appConfig = localStorage.getItem("appConfig");
      const parsedAppData = _appConfig ? JSON.parse(_appConfig) : {};

      const baseUrl = isV4Template()
        ? `${S3_URL}/${parsedAppData.xAppLocationCode}/${parsedAppData.xAppOperatorCode}`
        : isOAT
          ? `${S3_URL}/${appConfig?.locationId}/OAT`
          : `${S3_URL}/${appConfig?.locationId}/marketplace`;

      const waypointThemeURL =
        isOAT && waypointId
          ? `${baseUrl}/${waypointId}/theme/theme.json`
          : null;

      const defaultThemeURL = `${baseUrl}/theme/theme.json`;
      const fontBaseURL = `${S3_URL}/${appConfig?.locationId}`;

      const fetchWithTimeout = (url) => {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject("Timeout exceeded"), 10000)
        );

        const apiRequestPromise = callAPI.getS3(url).then((res) => {
          if (!res.ok) throw new Error("Failed request");
          return res.json();
        });

        return Promise.race([apiRequestPromise, timeoutPromise]);
      };

      let colorThemeResponse;

      if (waypointThemeURL) {
        try {
          colorThemeResponse = await fetchWithTimeout(waypointThemeURL);
        } catch (err) {
          console.warn("Waypoint theme failed, falling back...", err);
        }
      }

      if (!colorThemeResponse) {
        colorThemeResponse = await fetchWithTimeout(defaultThemeURL);
      }
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      if (colorThemeResponse) {
        setColorTheme(colorThemeResponse);
        fetchFontTheme(colorThemeResponse?.fontFamily, baseUrl, fontBaseURL, isOAT);
      } else {
        setColorTheme(DefaultColorTheme);
      }
    } catch (error) {
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      console.error("Theme fetch failed:", error);
      setColorTheme(DefaultColorTheme);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsColorTheme(true);
      }
    }
  };

  const fetchFontTheme = (fonts, baseUrl, fontBaseURL, isOAT = false) => {
    if (!fonts || fonts.length === 0) return;

    if (!isOAT && document.getElementById("dynamic-fonts")) return;

    const style = document.createElement("style");
    style.id = "dynamic-fonts";
    style.type = "text/css";

    fonts.forEach((font) => {
      const weights = Array.isArray(font.weight) ? font.weight : [font.weight];
      let URL = font?.isSharedFontFamily ? fontBaseURL : baseUrl;

      if (isV4Template()) {
        URL = `${S3_URL}`;
      }

      weights.forEach((weight) => {
        const fontFace = `
        @font-face {
          font-family: "${font.fontFamily}";
          src: url("${URL}${font.src}") format("${font?.format}");
          font-weight: ${weight};
          font-style: ${font?.style || "normal"};
          font-display: swap;
        }
      `;

        style.appendChild(document.createTextNode(fontFace));
      });
    });

    document.head.appendChild(style);
  };

  return (
    <ColorThemeContext.Provider value={colorTheme}>
      <ThemeProvider theme={colorTheme}>
        <GlobalStyles />
        {children}
      </ThemeProvider>
    </ColorThemeContext.Provider>
  );
};

export { ColorThemeProvider, ColorThemeContext };
